import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Dimensions, View } from 'react-native'
import ReactQuill from 'react-quill-new'
import v4 from 'uuid/v4'
import { useDispatch, useSelector } from 'react-redux'

import {
    setAssistantEnabled,
    setIsQuillTagEditorOpen,
    setQuillEditorProjectId,
    setQuillTextInputProjectIdsByEditorId,
} from '../../../redux/actions'
import CustomScrollView from '../../UIControls/CustomScrollView'
import AddFeedAttachButton from '../AddFeed/AddFeedAttachButton'
import WrapperMentionsModal from './WrapperMentionsModal'
import {
    ALLOWED_FORMATS,
    cleanTagsInteractionsPopus,
    COMMENT_MODAL_THEME,
    CREATE_PROJECT_THEME_DEFAULT,
    CREATE_SUBTASK_MODAL_THEME,
    CREATE_TASK_MODAL_THEME,
    createPlaceholder,
    createAppManagedFileUpload,
    getElementOffset,
    GOAL_THEME,
    MENTION_MODAL_CONTACTS_TAB,
    MENTION_MODAL_GOALS_TAB,
    MENTION_MODAL_NOTES_TAB,
    MENTION_MODAL_RIGHT_MARGIN,
    MENTION_MODAL_TASKS_TAB,
    MENTION_MODAL_TOPICS_TAB,
    MENTION_MODAL_WIDTH,
    NEW_TOPIC_MODAL_THEME,
    NOT_USER_MENTIONED,
    onCopy,
    processPastedText,
    processPastedTextWithBreakLines,
    QUILL_EDITOR_TEXT_INPUT_TYPE,
    SEARCH_THEME,
    SUBTASK_THEME,
    TASK_THEME,
    updateTextUsingDeltaOps,
    beforeUndoRedo,
    CREATE_PROJECT_THEME_MODERN,
    INVITE_THEME_MODERN,
    INVITE_THEME_DEFAULT,
    normalizeDictatedText,
    buildDictationDelta,
    buildResolvedPeopleMention,
    placeDictationCaret,
    insertAttachmentInsideEditor,
} from './textInputHelper'
import RambleButton from '../../UIControls/RambleButton'
import { isDictationSupported } from '../../../hooks/useRambleRecorder'
import { isDictationButtonVisible, shouldTrackDictationActivity } from './dictationVisibility'
import useDictationSubmit from './dictationSubmit'
import { unmountEmbedReactRoots } from './autoformat/formats/embedReactRoot'
import {
    ATTACHMENT_TRIGGER,
    IMAGE_TRIGGER,
    KARMA_TRIGGER,
    MENTION_SPACE_CODE,
    MILESTONE_TAG_TRIGGER,
    VIDEO_TRIGGER,
} from '../Utils/HelperFunctions'
import './styles.css'
import './quill2Setup'
import {
    formatUrl,
    getUrlObject,
    isValidContactLink,
    isValidGoalLink,
    isValidSkillLink,
    isValidNoteLink,
    isValidProjectLink,
    isValidTaskLink,
    isValidAssistantLink,
    isValidArtifactLink,
    getDvMainTabLink,
    getAppLinkOrigin,
} from '../../../utils/LinkingHelper'
import Backend from '../../../utils/BackendBridge'
import ProjectHelper, { ALL_PROJECTS_INDEX } from '../../SettingsView/ProjectsSettings/ProjectHelper'
import TasksHelper from '../../TaskListView/Utils/TasksHelper'
import { setTaskAssistant } from '../../../utils/backends/Tasks/tasksFirestore'
import { setNoteAssistant } from '../../../utils/backends/Notes/notesFirestore'
import { setUserAssistant } from '../../../utils/backends/Users/usersFirestore'
import { setContactAssistant, copyContactToProject } from '../../../utils/backends/Contacts/contactsFirestore'
import { setGoalAssistant } from '../../../utils/backends/Goals/goalsFirestore'
import { setSkillAssistant } from '../../../utils/backends/Skills/skillsFirestore'
import { updateChatAssistant } from '../../../utils/backends/Chats/chatsFirestore'
import { GLOBAL_PROJECT_ID, isGlobalAssistant } from '../../AdminPanel/Assistants/assistantsHelper'
import { createAutoExpandMeasurement, getNaturalEditorSize } from './autoExpandMeasurement'

const Delta = ReactQuill.Quill.import('delta')

export let quillTextInputRefs = {}
export let quillTextInputProjectIds = {}
export let quillTextInputIsCalendarTask = {}

function CustomTextInput3(
    {
        placeholder,
        onChangeText,
        onChangeDelta,
        fixedHeight,
        maxHeight,
        disabledEdition,
        containerStyle,
        projectIndex,
        projectId,
        setMentionsModalActive,
        initialTextExtended,
        styleTheme,
        singleLine,
        disabledTags,
        disabledMentions,
        onKeyPress,
        isMobileView,
        keyboardType,
        userIdAllowedToEditTags,
        inMentionsEditionTag,
        selectUserToMentionEditTag,
        inGenericTask,
        genericData,
        setInputCursorIndex,
        setEditor,
        initialDeltaOps,
        initialCursorIndex,
        disabledEnterKey,
        disabledTabKey,
        setInitialLinkedObject,
        otherFormats,
        externalEditorId,
        keepBreakLines,
        allowPlainEnterBreakLines,
        onCustomSelectionChange,
        isCalendarTask,
        forceTriggerEnterActionForBreakLines,
        characterLimit,
        setShowRunOutGoalModal,
        chatAssistantData,
        setAssistantId,
        autoFocus,
        focusOnInitialText = true,
        onMentionSelected,
        insertAssistantAsMention = false,
        onContentSizeChange,
        scrollEnabled = true,
        showScrollIndicator = true,
        autoExpand = false,
        hideDictation = false,
        dictationTargetKind,
        alwaysShowDictation = false,
        onDictationSubmit,
        // AT-2823: a press-and-hold handed over from another control (the board's floating "+"),
        // which this input's mic takes over as its own push-to-talk hold.
        pushToTalkHandoff,
        showAttachmentButton = false,
        // AT-2938: stack the in-field controls (+ above the mic) instead of laying them out in a
        // row. Used by the mobile assistant composers once the text has wrapped, so the text gets
        // the width of one control back. Only meaningful when both controls are shown.
        stackInlineControls = false,
        getMentionViewport,
    },
    ref
) {
    const dispatch = useDispatch()
    const showMentionPopupRef = useRef(false)
    const quillRef = useRef(null)
    const reactQuillRef = useRef(null)
    const selectionRef = useRef({ index: 0, length: 0 })
    const mentionTextRef = useRef('')
    const quillKeyboardBindingsTabRef = useRef(null)
    const autoExpandMeasurementRef = useRef(null)
    const onContentSizeChangeRef = useRef(onContentSizeChange)

    onContentSizeChangeRef.current = onContentSizeChange

    const gold = useSelector(state => state.loggedUser.gold)
    const loggedUserId = useSelector(state => state.loggedUser.uid)
    const loggedUserProjects = useSelector(state => state.loggedUserProjects)

    const [userEditingTagsId, setUserEditingTagsId] = useState(
        userIdAllowedToEditTags ? userIdAllowedToEditTags : loggedUserId
    )
    const [editorId, setEditorId] = useState(externalEditorId ? externalEditorId : v4())
    // Marks this input's own scroll view: Quill may scroll inside it to follow
    // the caret, but never anything above it. See quill2Setup.js (AT-2220).
    const scrollBoundaryId = `ql-scroll-boundary-${editorId}`
    const [editorElement, setEditorElement] = useState(null)
    const [containerElement, setContainerElement] = useState(null)
    const [selectionBounds, setSelectionBounds] = useState({ top: 0, left: 0 })
    const [html, setHtmlState] = useState('')
    /**
     * `html` is what ReactQuill is given as its controlled `value`, and
     * react-quill overwrites the editor whenever that value disagrees with the
     * editor's own contents (`shouldComponentUpdate` -> `setEditorContents`).
     *
     * React state alone is too slow for that contract: this component also
     * writes into the editor imperatively (`setContents(initialDeltaOps)` at
     * mount, `updateContents` in processInitialText/clearAndSetContent), and a
     * re-render triggered from anywhere else - a redux dispatch, or the
     * synchronous relayout CustomScrollView performs after measuring - can be
     * flushed BEFORE the queued `setHtml` is applied. react-quill then sees the
     * stale value, decides the editor is out of sync and wipes it. That is
     * AT-2178: the create-task popup was pre-filled correctly and then emptied
     * again a tick later.
     *
     * The ref is updated synchronously, so every render - including one that
     * interleaves with a pending state update - passes the value the editor
     * actually holds. The state is kept because other effects depend on it.
     */
    const htmlRef = useRef('')
    const setHtml = value => {
        htmlRef.current = value
        setHtmlState(value)
    }
    const [mentionModalHeight, setMentionModalHeight] = useState(0)
    const [flag, setFlag] = useState(false)

    projectIndex =
        projectId === GLOBAL_PROJECT_ID
            ? ALL_PROJECTS_INDEX
            : projectIndex
              ? projectIndex
              : projectId && ProjectHelper.getProjectIndexById(projectId)
    const innerProjectId = projectId ? projectId : projectIndex ? loggedUserProjects[projectIndex].id : undefined

    const textRef = useRef('')
    const mentionStartIndexRef = useRef(0)
    const mentionLastHalfTextRef = useRef('')

    const processPastedFn = keepBreakLines ? processPastedTextWithBreakLines : processPastedText

    const allowMentions = !disabledTags && !disabledMentions && !inMentionsEditionTag

    //DICTATION (rambler)
    const selectionTouchedRef = useRef(false)
    const [dictationVisible, setDictationVisible] = useState(false)
    const dictationEnabled =
        !hideDictation &&
        !disabledEdition &&
        !inMentionsEditionTag &&
        styleTheme !== SEARCH_THEME &&
        !!innerProjectId &&
        isDictationSupported()
    // Task/goal-name editors are title inputs even though they are not `singleLine` — they accept
    // breaklines only to convert them into the Enter action (i.e. submit), so dictated text must
    // arrive as a single line there too or a stray '\n' would submit the task mid-insert.
    const dictationIsTitleTarget =
        singleLine ||
        [
            TASK_THEME,
            SUBTASK_THEME,
            GOAL_THEME,
            CREATE_TASK_MODAL_THEME,
            CREATE_SUBTASK_MODAL_THEME,
            NEW_TOPIC_MODAL_THEME,
        ].includes(styleTheme)
    const dictationKind = dictationTargetKind || (dictationIsTitleTarget ? 'title' : 'generic')

    useEffect(() => {
        if (!dictationEnabled || !editorElement) return
        // Reserve the mic strip in the text layout (see .ql-editorWithDictation in styles.css).
        editorElement.classList.add('ql-editorWithDictation')
        return () => editorElement.classList.remove('ql-editorWithDictation')
    }, [dictationEnabled, editorElement])

    // Composer inputs (assistant line, chat) pin the mic on: on touch there is no hover, so the
    // activity rule below only reveals it once the field is already focused — AT-2355.
    const dictationButtonVisible = isDictationButtonVisible({
        alwaysShowDictation,
        activityVisible: dictationVisible,
    })

    useEffect(() => {
        if (!shouldTrackDictationActivity({ dictationEnabled, alwaysShowDictation }) || !containerElement) return
        // The mic overlay lives in CustomScrollView's fixedChildren, OUTSIDE the Quill container —
        // so visibility tracks the whole input frame (the scroll-boundary element), not
        // ql-container: moving the pointer onto the overlay itself must not count as leaving.
        const frameElement = document.getElementById(scrollBoundaryId)
        if (!frameElement) return

        const activate = () => setDictationVisible(true)
        const onFocusOut = event => {
            if (event.relatedTarget && frameElement.contains(event.relatedTarget)) return
            setDictationVisible(false)
        }
        const onMouseLeave = () => {
            if (frameElement.contains(document.activeElement)) return
            setDictationVisible(false)
        }
        // The listeners attach AFTER the mount effect has already autofocused the editor, and the
        // pointer that clicked the input is usually still inside the frame — so neither focusin
        // nor mouseenter will fire until the user happens to leave and re-enter. Initialize from
        // the current state or the mic stays invisible on exactly the surfaces that autofocus
        // (add goal/contact/topic — AT: rambler QA).
        const alreadyActive = (() => {
            if (frameElement.contains(document.activeElement)) return true
            try {
                return frameElement.matches(':hover')
            } catch (error) {
                return false
            }
        })()
        if (alreadyActive) setDictationVisible(true)
        frameElement.addEventListener('focusin', activate)
        frameElement.addEventListener('focusout', onFocusOut)
        frameElement.addEventListener('mouseenter', activate)
        frameElement.addEventListener('mouseleave', onMouseLeave)
        return () => {
            frameElement.removeEventListener('focusin', activate)
            frameElement.removeEventListener('focusout', onFocusOut)
            frameElement.removeEventListener('mouseenter', activate)
            frameElement.removeEventListener('mouseleave', onMouseLeave)
        }
    }, [dictationEnabled, alwaysShowDictation, containerElement])

    // Whether the last transcript actually made it into the editor. A push-to-talk submit is only
    // allowed to fire when it did: a transcript that normalizes away to nothing (whitespace, a
    // lone newline in a title input) would otherwise submit the UNTOUCHED draft — i.e. holding the
    // mic and saying nothing would send whatever the user had half-typed.
    const dictationInsertedRef = useRef(false)

    // Cancels the deferred caret re-assertion scheduled by the last `placeDictationCaret`. That
    // deferred call also re-focuses the editor, which a submitting dictation must not do (AT-2422).
    const cancelDictationCaretRef = useRef(null)

    const armDictationSubmit = useDictationSubmit({ onDictationSubmit, forceTriggerEnterActionForBreakLines })

    const insertDictatedText = text => {
        dictationInsertedRef.current = false
        const editor = quillRef.current
        if (!editor) return
        const cleaned = normalizeDictatedText(text, dictationIsTitleTarget)
        if (!cleaned) return

        const liveSelection = editor.hasFocus() ? editor.getSelection() : null
        // With no live selection fall back to the last known caret; if the editor was never
        // focused at all, append at the end rather than pushing dictation before existing text.
        const fallback = selectionTouchedRef.current
            ? selectionRef.current
            : { index: Math.max(0, editor.getLength() - 1), length: 0 }
        const { index, length } = liveSelection || fallback

        const contentDelta = processPastedFn(
            cleaned,
            Delta,
            innerProjectId,
            editorId,
            userEditingTagsId,
            inGenericTask,
            genericData,
            editor,
            false,
            null,
            false
        )
        const charBefore = index > 0 ? editor.getText(index - 1, 1) : ''
        const needsLeadingSpace = !!charBefore && !/\s/.test(charBefore)
        const { delta, caretIndex } = buildDictationDelta({ Delta, contentDelta, index, length, needsLeadingSpace })
        // 'user' source: runs the normal onChange path (htmlRef stays in sync — AT-2178, and
        // onChangeText reaches the parent's save flow) and lands in the undo history.
        editor.updateContents(delta, 'user')
        dictationInsertedRef.current = true
        cancelDictationCaretRef.current = placeDictationCaret(editor, caretIndex, () => quillRef.current === editor)
    }

    // Push-to-talk (AT-2405): holding the mic inserts the transcript AND fires this input's own
    // Enter action. The insertion above only QUEUES the host's state update, so the submit is
    // armed rather than called — `useDictationSubmit` fires it after the next commit, by which
    // time the host's Enter action can see the dictated text. Submitting synchronously here ran
    // the host's pre-dictation closure instead, which in the add-new-task field dismissed the
    // editor and created nothing; see dictationSubmit.js.
    //
    // `textRef` is written synchronously by `updateText`, so it already holds draft + transcript
    // — that is what has to be sent, not the transcript alone, or a half-typed message would be
    // silently thrown away.
    //
    // The caret re-assertion queued by the insertion above is dropped here (AT-2422). It is a
    // deferred `focus()` + `setSelection()` on a macrotask, while this submit runs from a
    // post-commit effect that React flushes through the Scheduler — so the host's send, and the
    // `blur()` + `Keyboard.dismiss()` that go with it, land FIRST and the timer then puts focus
    // straight back, re-opening the keyboard on a field the user has just emptied by voice. The
    // caret it was protecting is meaningless on this path: the text is leaving the input. Only the
    // submitting path cancels, so tapping the mic without holding still ends with the caret placed
    // after the transcript, ready to keep typing.
    const submitDictatedText = () => {
        if (!dictationInsertedRef.current) return
        cancelDictationCaretRef.current?.()
        cancelDictationCaretRef.current = null
        armDictationSubmit(() => textRef.current)
    }

    //MENTIONS
    const tryToOpenMentionModalBySelection = contentLength => {
        if (allowMentions && contentLength === textRef.current.length) {
            const previousCharacter = textRef.current[selectionRef.current.index - 1]
            if (previousCharacter === '@') {
                const previousPreviousCharacter = textRef.current[selectionRef.current.index - 2]
                if (!previousPreviousCharacter || previousPreviousCharacter.match(/\s|&/)) {
                    startToMention(textRef.current, selectionRef.current.index)
                }
            }
        }
    }

    const startToMention = (text, cursorIndex) => {
        showMentionPopupRef.current = true
        if (setMentionsModalActive) {
            setMentionsModalActive(true)
        }

        mentionStartIndexRef.current = cursorIndex

        let lastHalfText = text.substring(cursorIndex)
        if (lastHalfText[0] === '&') {
            mentionTextRef.current = ''
            mentionLastHalfTextRef.current = lastHalfText
        } else {
            const wordsInLastHalf = lastHalfText.split(/\s|&/)
            mentionTextRef.current = wordsInLastHalf[0] ? wordsInLastHalf[0] : ''
            const newCursorIndex = cursorIndex + wordsInLastHalf[0].length
            lastHalfText = text.substring(newCursorIndex)
            mentionLastHalfTextRef.current = lastHalfText
            setTimeout(() => {
                quillRef.current.setSelection(newCursorIndex, 0, 'user')
            })
        }
    }

    const updateMentionText = text => {
        if (showMentionPopupRef.current) {
            mentionTextRef.current = text.substring(
                mentionStartIndexRef.current,
                text.length - mentionLastHalfTextRef.current.length
            )
        }
    }

    const checkMentionModalState = contentLength => {
        if (showMentionPopupRef.current) {
            const { index: cursorIndex, length: selectionLength } = selectionRef.current
            const mentionEndIndex = contentLength - mentionLastHalfTextRef.current.length

            if (cursorIndex < mentionStartIndexRef.current || cursorIndex + selectionLength > mentionEndIndex) {
                insertNormalMention()
            }
        } else {
            tryToOpenMentionModalBySelection(contentLength)
        }
    }

    const insertNormalMention = () => {
        const mentionText = mentionTextRef.current
        const mentionPosition = mentionStartIndexRef.current

        closeMentionPopup()
        const cleanText = mentionText.trim()
        if (cleanText.length > 0 && !inMentionsEditionTag) {
            selectionRef.current = { index: mentionPosition - 1, length: 0 }
            let parsedText = cleanText.replaceAll(/\s+/g, ' ')
            parsedText = parsedText.replaceAll(' ', MENTION_SPACE_CODE)
            const mention = {
                text: parsedText,
                id: v4(),
                userId: NOT_USER_MENTIONED,
                editorId,
                userEditingTagsId,
            }
            const delta = new Delta()
            delta.retain(selectionRef.current.index)
            delta.insert({ mention })
            delta.insert(' ')
            delta.delete(mentionText.length + 1)
            quillRef.current.updateContents(delta, 'user')
            if (quillRef.current.getSelection().length > 0) {
                quillRef.current.setSelection(selectionRef.current.index, 0, 'user')
            }
        }
        setFlag(!flag)
    }

    const selectItemToMention = async (item, activeTab) => {
        if (onMentionSelected) onMentionSelected(item, activeTab)
        if (selectUserToMentionEditTag) {
            selectUserToMentionEditTag(item)
        } else if (activeTab === MENTION_MODAL_CONTACTS_TAB) {
            if (item.isAssistant && insertAssistantAsMention) {
                selectionRef.current = { index: mentionStartIndexRef.current - 1, length: 0 }
                const mention = buildResolvedPeopleMention(item, v4(), editorId, userEditingTagsId)
                const delta = new Delta()
                delta.retain(selectionRef.current.index)
                delta.insert({ mention })
                delta.insert(' ')
                delta.delete(mentionTextRef.current.length + 1)
                quillRef.current.updateContents(delta, 'user')
            } else if (item.isAssistant) {
                const { uid: assistantId } = item
                selectionRef.current = { index: mentionStartIndexRef.current - 1, length: 0 }
                const assistantUrl = `${getAppLinkOrigin()}${getDvMainTabLink(projectId, assistantId, 'assistants')}`
                const execRes = formatUrl(assistantUrl)
                if (execRes) {
                    const url = getUrlObject(assistantUrl, execRes, projectId, editorId, userIdAllowedToEditTags)
                    const delta = new Delta()
                    delta.retain(mentionStartIndexRef.current - 1)
                    delta.insert({ url })
                    delta.insert(' ')
                    delta.delete(mentionTextRef.current.length + 1)
                    quillRef.current.updateContents(delta, 'user')

                    if (chatAssistantData) {
                        const { objectId, objectAssistantId, objectType } = chatAssistantData
                        if (objectAssistantId !== assistantId) {
                            setAssistantId?.(assistantId)
                            switch (objectType) {
                                case 'tasks':
                                    await setTaskAssistant(projectId, objectId, assistantId, true)
                                    break
                                case 'notes':
                                    await setNoteAssistant(projectId, objectId, assistantId, true)
                                    break
                                case 'contacts':
                                    const isUser = !!TasksHelper.getUserInProject(projectId, objectId)
                                    if (isUser) {
                                        await setUserAssistant(projectId, objectId, assistantId, true)
                                    } else {
                                        await setContactAssistant(projectId, objectId, assistantId, true)
                                    }
                                    break
                                case 'users':
                                    await setUserAssistant(projectId, objectId, assistantId, true)
                                    break
                                case 'goals':
                                    await setGoalAssistant(projectId, objectId, assistantId, true)
                                    break
                                case 'skills':
                                    await setSkillAssistant(projectId, objectId, assistantId, true)
                                    break
                                case 'artifacts': {
                                    // Lazy: every editor in the app renders this input.
                                    const {
                                        setArtifactAssistant,
                                    } = require('../../../utils/backends/Artifacts/artifactsFirestore')
                                    await setArtifactAssistant(projectId, objectId, assistantId)
                                    break
                                }
                                case 'chats':
                                case 'topics':
                                    await updateChatAssistant(projectId, objectId, assistantId)
                                    break
                            }
                        }
                        if (gold > 0) {
                            dispatch(setAssistantEnabled(true))
                        } else {
                            if (setShowRunOutGoalModal) setShowRunOutGoalModal(true)
                        }
                    }
                }
            } else {
                // Copy contact to current project if selected from a different project
                let contactUserId = item.uid
                if (item.projectId && innerProjectId && item.projectId !== innerProjectId) {
                    const copiedContact = await copyContactToProject(innerProjectId, item)
                    if (copiedContact) contactUserId = copiedContact.uid
                }

                const contactName = item.displayName.replaceAll(' ', MENTION_SPACE_CODE)
                selectionRef.current = { index: mentionStartIndexRef.current - 1, length: 0 }
                const mention = { text: contactName, id: v4(), userId: contactUserId, editorId, userEditingTagsId }
                const delta = new Delta()
                delta.retain(mentionStartIndexRef.current - 1)
                delta.insert({ mention })
                delta.insert(' ')
                delta.delete(mentionTextRef.current.length + 1)
                quillRef.current.updateContents(delta, 'user')
            }
        } else if (activeTab === MENTION_MODAL_TASKS_TAB) {
            if (item.isPreConfigTask) {
                // Pre-configured task from AI assistant - create special URL
                const { id: taskId, assistantId, name: taskName } = item
                const assistantProjectId = isGlobalAssistant(assistantId) ? GLOBAL_PROJECT_ID : projectId
                selectionRef.current = { index: mentionStartIndexRef.current - 1, length: 0 }

                const preConfigTaskUrl = `${getAppLinkOrigin()}${getDvMainTabLink(
                    projectId,
                    taskId,
                    'preConfigTasks'
                )}?assistantId=${assistantId}&assistantProjectId=${assistantProjectId}&name=${encodeURIComponent(
                    taskName || ''
                )}`

                const execRes = formatUrl(preConfigTaskUrl)
                if (execRes) {
                    const url = getUrlObject(preConfigTaskUrl, execRes, projectId, editorId, userIdAllowedToEditTags)
                    const delta = new Delta()
                    delta.retain(mentionStartIndexRef.current - 1)
                    delta.insert({ url })
                    delta.insert(' ')
                    delta.delete(mentionTextRef.current.length + 1)
                    quillRef.current.updateContents(delta, 'user')
                }
            } else {
                // Regular task
                const { id } = item
                selectionRef.current = { index: mentionStartIndexRef.current - 1, length: 0 }
                const taskUrl = `${getAppLinkOrigin()}${getDvMainTabLink(projectId, id, 'tasks')}`
                const execRes = formatUrl(taskUrl)
                if (execRes) {
                    const url = getUrlObject(taskUrl, execRes, projectId, editorId, userIdAllowedToEditTags)
                    const delta = new Delta()
                    delta.retain(mentionStartIndexRef.current - 1)
                    delta.insert({ url })
                    delta.insert(' ')
                    delta.delete(mentionTextRef.current.length + 1)
                    quillRef.current.updateContents(delta, 'user')
                }
            }
        } else if (activeTab === MENTION_MODAL_NOTES_TAB) {
            const { id } = item
            const noteProjectId = item.projectId || projectId
            selectionRef.current = { index: mentionStartIndexRef.current - 1, length: 0 }
            const noteUrl = `${getAppLinkOrigin()}${getDvMainTabLink(noteProjectId, id, 'notes')}`
            const execRes = formatUrl(noteUrl)
            if (execRes) {
                const url = getUrlObject(noteUrl, execRes, noteProjectId, editorId, userIdAllowedToEditTags)
                const delta = new Delta()
                delta.retain(mentionStartIndexRef.current - 1)
                delta.insert({ url })
                delta.insert(' ')
                delta.delete(mentionTextRef.current.length + 1)
                quillRef.current.updateContents(delta, 'user')
            }
        } else if (activeTab === MENTION_MODAL_TOPICS_TAB) {
            const { id } = item
            selectionRef.current = { index: mentionStartIndexRef.current - 1, length: 0 }
            const topicUrl = `${getAppLinkOrigin()}${getDvMainTabLink(projectId, id, 'chats')}`
            const execRes = formatUrl(topicUrl)
            if (execRes) {
                const url = getUrlObject(topicUrl, execRes, projectId, editorId, userIdAllowedToEditTags)
                const delta = new Delta()
                delta.retain(mentionStartIndexRef.current - 1)
                delta.insert({ url })
                delta.insert(' ')
                delta.delete(mentionTextRef.current.length + 1)
                quillRef.current.updateContents(delta, 'user')
            }
        } else if (activeTab === MENTION_MODAL_GOALS_TAB) {
            const { id } = item
            selectionRef.current = { index: mentionStartIndexRef.current - 1, length: 0 }
            const goalUrl = `${getAppLinkOrigin()}${getDvMainTabLink(projectId, id, 'goals')}`
            const execRes = formatUrl(goalUrl)
            if (execRes) {
                const url = getUrlObject(goalUrl, execRes, projectId, editorId, userIdAllowedToEditTags)
                const delta = new Delta()
                delta.retain(mentionStartIndexRef.current - 1)
                delta.insert({ url })
                delta.insert(' ')
                delta.delete(mentionTextRef.current.length + 1)
                quillRef.current.updateContents(delta, 'user')
            }
        }

        setTimeout(() => {
            closeMentionPopup()
            setFlag(prev => !prev)
            quillRef.current.focus()
        })
    }

    const closeMentionPopup = () => {
        if (showMentionPopupRef.current) {
            showMentionPopupRef.current = false
            if (setMentionsModalActive) {
                setMentionsModalActive(false)
            }
            mentionStartIndexRef.current = 0
            mentionLastHalfTextRef.current = ''
            mentionTextRef.current = ''
        }
    }

    useEffect(() => {
        if (disabledEdition && showMentionPopupRef.current) {
            closeMentionPopup()
            setFlag(previous => !previous)
        }
    }, [disabledEdition])

    const getMentionModalLocation = selectionIndex => {
        const mentionModalParentOffset = getElementOffset(document.body)
        const editorOffset = getElementOffset(editorElement)

        const { bottom, left } = quillRef.current.getBounds(selectionIndex)

        const windowWidth = Dimensions.get('window').width
        const windowHeight = Dimensions.get('window').height

        let bounds = {}
        if (inMentionsEditionTag) {
            bounds = {
                left: editorOffset.left - mentionModalParentOffset.left - 61,
                top: bottom + (editorOffset.top - mentionModalParentOffset.top) + 16,
            }
        } else {
            bounds = {
                left: left + editorOffset.left - mentionModalParentOffset.left,
                top: bottom + (editorOffset.top - mentionModalParentOffset.top),
            }
        }

        const rightBoundary = windowWidth - MENTION_MODAL_WIDTH - MENTION_MODAL_RIGHT_MARGIN
        const bottomBoundary = windowHeight - mentionModalHeight - MENTION_MODAL_RIGHT_MARGIN
        if (bounds.left + mentionModalParentOffset.left > rightBoundary) {
            bounds.left = rightBoundary - mentionModalParentOffset.left
        }

        if (bounds.top + mentionModalParentOffset.top > bottomBoundary) {
            bounds.top = bottomBoundary - mentionModalParentOffset.top
        }
        const spaceBetweenAtAndModal = 12
        bounds.top += spaceBetweenAtAndModal
        setSelectionBounds(bounds)
    }

    useEffect(() => {
        if (mentionModalHeight > 0 && (!showMentionPopupRef.current || inMentionsEditionTag)) {
            getMentionModalLocation(
                selectionRef.current.index === 0 ? textRef.current.length : selectionRef.current.index
            )
        }
    }, [mentionModalHeight])

    //MAIN FUNCTIONS
    const onChangeSelection = (selection, source, content) => {
        if (selection && editorElement) {
            onCustomSelectionChange?.(selection, quillRef)
            const { index, length } = selection
            selectionRef.current = { index, length }
            selectionTouchedRef.current = true
        }

        if (!disabledTags && !disabledMentions && selection && editorElement) {
            if (!showMentionPopupRef.current || inMentionsEditionTag) {
                getMentionModalLocation(selectionRef.current.index)
            }
        }

        if (inMentionsEditionTag && !showMentionPopupRef.current) {
            showMentionPopupRef.current = true
            setMentionsModalActive(true)
        }

        if (disabledTags || inMentionsEditionTag) {
            return
        }

        if (!disabledMentions && selection && editorElement) {
            const contentLength = content.getLength() - 1
            checkMentionModalState(contentLength)
        }

        if (selection && setInputCursorIndex) {
            setInputCursorIndex(selection.index)
        }
    }

    const notifyTextChange = (
        extendedText,
        linkedNotesUrls,
        linkedTasksUrls,
        linkedContactsUrls,
        linkedProjectsUrls,
        linkedGoalsUrls,
        linkedSkillsUrls,
        linkedAssistantUrls,
        linkedArtifactsUrls
    ) => {
        if (!onChangeText) return

        if (onChangeText.length <= 1) {
            onChangeText(extendedText)
            return
        }

        onChangeText(
            extendedText,
            linkedNotesUrls,
            linkedTasksUrls,
            linkedContactsUrls,
            linkedProjectsUrls,
            linkedGoalsUrls,
            linkedSkillsUrls,
            linkedAssistantUrls,
            linkedArtifactsUrls
        )
    }

    const updateText = (htmlContent, delta) => {
        setHtml(htmlContent)
        const newDelta = onChangeDelta?.(delta, quillRef, editorId, userEditingTagsId) || delta
        const newText = updateTextUsingDeltaOps(newDelta.ops, textRef.current, value => {
            textRef.current = value
        })

        if (forceTriggerEnterActionForBreakLines) {
            for (let i = 0; i < newDelta.ops.length; i++) {
                const op = newDelta.ops[i]
                if (op.insert && op.insert === '\n') {
                    forceTriggerEnterActionForBreakLines()
                    break
                }
            }
        }

        if (!disabledMentions) {
            updateMentionText(newText)
        }

        if (!showMentionPopupRef.current && allowMentions) {
            const lastCharacter = newText[selectionRef.current.index - 1]
            const previousToLastCharacter = newText[selectionRef.current.index - 2]
            const previousPreviousToLastCharacter = newText[selectionRef.current.index - 3]
            const nextCharacter = newText[selectionRef.current.index]

            if (lastCharacter === '@' && (!previousToLastCharacter || previousToLastCharacter.match(/\s|&/))) {
                startToMention(newText, selectionRef.current.index)
            } else if (nextCharacter === '@' && (!lastCharacter || lastCharacter.match(/\s|&/))) {
                startToMention(newText, selectionRef.current.index + 1)
            } else if (
                lastCharacter &&
                lastCharacter !== '@' &&
                !lastCharacter.match(/\s|&/) &&
                previousToLastCharacter === '@' &&
                (!previousPreviousToLastCharacter || previousPreviousToLastCharacter.match(/\s|&/))
            ) {
                startToMention(newText, selectionRef.current.index - 1)
            }
        }

        if (inMentionsEditionTag && !showMentionPopupRef.current) {
            showMentionPopupRef.current = true
            setMentionsModalActive(true)
        }

        if (disabledTags || inMentionsEditionTag) {
            if (onChangeText) {
                onChangeText(newText)
            }
            processCharacterLimit()
            return
        }

        if (onChangeText) {
            const {
                extendedText,
                linkedTasksUrls,
                linkedNotesUrls,
                linkedContactsUrls,
                linkedProjectsUrls,
                linkedGoalsUrls,
                linkedSkillsUrls,
                linkedAssistantUrls,
                linkedArtifactsUrls,
            } = getFinalText()
            notifyTextChange(
                extendedText,
                linkedNotesUrls,
                linkedTasksUrls,
                linkedContactsUrls,
                linkedProjectsUrls,
                linkedGoalsUrls,
                linkedSkillsUrls,
                linkedAssistantUrls,
                linkedArtifactsUrls
            )
        }

        processCharacterLimit()
    }

    const processCharacterLimit = () => {
        if (characterLimit) {
            const length = quillRef.current.getLength()
            if (length > characterLimit) quillRef.current.deleteText(characterLimit, length)
        }
    }

    const processInitialText = () => {
        if (initialTextExtended && initialTextExtended.length > 0) {
            let delta = new Delta()
            if (disabledTags || inMentionsEditionTag) {
                delta.insert(initialTextExtended)
            } else {
                if (isCalendarTask) {
                    delta = reactQuillRef.current.getEditor().clipboard.convert({ html: initialTextExtended })
                } else {
                    delta = processPastedFn(
                        initialTextExtended,
                        Delta,
                        innerProjectId,
                        editorId,
                        userEditingTagsId,
                        inGenericTask,
                        genericData,
                        quillRef.current,
                        false,
                        null,
                        false
                    )
                }
            }

            quillRef.current.updateContents(delta)
            // setSelection also focuses Quill. Some prefilled editors, such as the
            // description in Settings, must stay unfocused when their view opens.
            if (focusOnInitialText) quillRef.current.setSelection(initialTextExtended.length, 0)
            setInitialLinkedObject && getLinkedUrlInitialText()
        }
    }

    const getFinalText = () => {
        if (disabledTags || inMentionsEditionTag) {
            return textRef.current
        }
        const deltaContent = quillRef.current.getContents()
        let finalText = ''
        let finalTextExtended = ''
        const linkedNotesUrls = []
        const linkedTasksUrls = []
        const linkedContactsUrls = []
        const linkedProjectsUrls = []
        const linkedGoalsUrls = []
        const linkedSkillsUrls = []
        const linkedAssistantUrls = []
        const linkedArtifactsUrls = []

        for (let i = 0; i < deltaContent.ops.length; i++) {
            const op = deltaContent.ops[i]
            const { insert } = op
            const {
                mention,
                hashtag,
                email,
                url,
                commentTagFormat,
                attachment,
                customImageFormat,
                videoFormat,
                karma,
                milestoneTag,
            } = insert

            let beforeSpace = ''
            if (i > 0) {
                const previousInsert = deltaContent.ops[i - 1].insert
                if (
                    typeof previousInsert !== 'string' ||
                    previousInsert.length === 0 ||
                    previousInsert[previousInsert.length - 1] !== ' '
                ) {
                    beforeSpace = ' '
                }
            }

            let afterSpace = ''
            if (i + 1 < deltaContent.ops.length) {
                const nextInsert = deltaContent.ops[i + 1].insert
                if (typeof nextInsert !== 'string' || nextInsert.length === 0 || nextInsert[0] !== ' ') {
                    afterSpace = ' '
                }
            }

            if (commentTagFormat) {
                const commentTagFormatText = `&${commentTagFormat.text}`
                finalText += beforeSpace + commentTagFormatText + afterSpace
                finalTextExtended += beforeSpace + commentTagFormatText + afterSpace
            } else if (mention) {
                const mentionText = `@${mention.text.replaceAll(' ', MENTION_SPACE_CODE)}`
                const mextionTextExtended =
                    mention.userId === NOT_USER_MENTIONED ? mentionText : `${mentionText}#${mention.userId}`
                finalText += beforeSpace + mentionText + afterSpace
                finalTextExtended += beforeSpace + mextionTextExtended + afterSpace

                if (mention.userId !== NOT_USER_MENTIONED) {
                    const objectType = TasksHelper.getPeopleTypeUsingId(mention.userId, innerProjectId)
                    const contactUrl = `${window.origin}${getDvMainTabLink(innerProjectId, mention.userId, objectType)}`
                    if (linkedContactsUrls.indexOf(contactUrl) < 0) {
                        linkedContactsUrls.push(contactUrl)
                    }
                }
            } else if (karma) {
                const karmaText = `${KARMA_TRIGGER}${karma.userId}`
                finalText += beforeSpace + karmaText + afterSpace
                finalTextExtended += beforeSpace + karmaText + afterSpace
            } else if (milestoneTag) {
                const milestoneText = `${MILESTONE_TAG_TRIGGER}${milestoneTag.text}${MILESTONE_TAG_TRIGGER}${milestoneTag.milestoneId}`
                finalText += beforeSpace + milestoneText + afterSpace
                finalTextExtended += beforeSpace + milestoneText + afterSpace
            } else if (attachment) {
                const attachmentText = `${ATTACHMENT_TRIGGER}${attachment.uri}${ATTACHMENT_TRIGGER}${attachment.text}${ATTACHMENT_TRIGGER}${attachment.isNew}`
                finalText += beforeSpace + attachment.text + afterSpace
                finalTextExtended += beforeSpace + attachmentText + afterSpace
            } else if (customImageFormat) {
                const imageText = `${IMAGE_TRIGGER}${customImageFormat.uri}${IMAGE_TRIGGER}${customImageFormat.resizedUri}${IMAGE_TRIGGER}${customImageFormat.text}${IMAGE_TRIGGER}${customImageFormat.isNew}`
                finalText += beforeSpace + customImageFormat.text + afterSpace
                finalTextExtended += beforeSpace + imageText + afterSpace
            } else if (videoFormat) {
                const videoText = `${VIDEO_TRIGGER}${videoFormat.uri}${VIDEO_TRIGGER}${videoFormat.text}${VIDEO_TRIGGER}${videoFormat.isNew}`
                finalText += beforeSpace + videoFormat.text + afterSpace
                finalTextExtended += beforeSpace + videoText + afterSpace
            } else if (hashtag) {
                const hashtagText = `#${hashtag.text}`
                finalText += beforeSpace + hashtagText + afterSpace
                finalTextExtended += beforeSpace + hashtagText + afterSpace
            } else if (email) {
                finalText += beforeSpace + email.text + afterSpace
                finalTextExtended += beforeSpace + email.text + afterSpace
            } else if (url) {
                finalText += beforeSpace + url.url + afterSpace
                finalTextExtended += beforeSpace + url.url + afterSpace
                if (isValidTaskLink(url.url, innerProjectId) && linkedTasksUrls.indexOf(url.url) < 0)
                    linkedTasksUrls.push(url.url)
                else if (isValidNoteLink(url.url, innerProjectId) && linkedNotesUrls.indexOf(url.url) < 0)
                    linkedNotesUrls.push(url.url)
                else if (isValidContactLink(url.url, innerProjectId) && linkedContactsUrls.indexOf(url.url) < 0)
                    linkedContactsUrls.push(url.url)
                else if (isValidProjectLink(url.url, innerProjectId) && linkedProjectsUrls.indexOf(url.url) < 0)
                    linkedProjectsUrls.push(url.url)
                else if (isValidGoalLink(url.url, innerProjectId) && linkedGoalsUrls.indexOf(url.url) < 0)
                    linkedGoalsUrls.push(url.url)
                else if (isValidSkillLink(url.url, innerProjectId) && linkedSkillsUrls.indexOf(url.url) < 0)
                    linkedSkillsUrls.push(url.url)
                else if (isValidAssistantLink(url.url, innerProjectId) && linkedAssistantUrls.indexOf(url.url) < 0)
                    linkedAssistantUrls.push(url.url)
                else if (isValidArtifactLink(url.url, innerProjectId) && linkedArtifactsUrls.indexOf(url.url) < 0)
                    linkedArtifactsUrls.push(url.url)
            } else {
                if (isValidTaskLink(insert, innerProjectId) && linkedTasksUrls.indexOf(insert) < 0)
                    linkedTasksUrls.push(insert)
                else if (isValidNoteLink(insert, innerProjectId) && linkedNotesUrls.indexOf(insert) < 0)
                    linkedNotesUrls.push(insert)
                else if (isValidContactLink(insert, innerProjectId) && linkedContactsUrls.indexOf(insert))
                    linkedContactsUrls.push(insert)
                else if (isValidProjectLink(insert, innerProjectId) && linkedProjectsUrls.indexOf(insert))
                    linkedProjectsUrls.push(insert)
                else if (isValidGoalLink(insert, innerProjectId) && linkedGoalsUrls.indexOf(insert))
                    linkedGoalsUrls.push(insert)
                else if (isValidSkillLink(insert, innerProjectId) && linkedSkillsUrls.indexOf(insert))
                    linkedSkillsUrls.push(insert)
                else if (isValidAssistantLink(insert, innerProjectId) && linkedAssistantUrls.indexOf(insert))
                    linkedAssistantUrls.push(insert)
                else if (isValidArtifactLink(insert, innerProjectId) && linkedArtifactsUrls.indexOf(insert) < 0)
                    linkedArtifactsUrls.push(insert)
                finalText += insert
                finalTextExtended += insert
            }
        }

        finalText = finalText.substring(0, finalText.length - 1)
        finalTextExtended = finalTextExtended.substring(0, finalTextExtended.length - 1)
        return {
            extendedText: finalTextExtended,
            linkedTasksUrls,
            linkedNotesUrls,
            linkedContactsUrls,
            linkedProjectsUrls,
            linkedGoalsUrls,
            linkedSkillsUrls,
            linkedAssistantUrls,
            linkedArtifactsUrls,
        }
    }

    //OTHERS
    const getLinkedUrlInitialText = () => {
        let initialLinkedTasksUrls = []
        let initialNotesUrls = []
        let initialContactsUrls = []
        let initialProjectsUrls = []
        let initialGoalsUrls = []
        let initialSkillsUrls = []
        let initialAssistantUrls = []

        const deltaContent = quillRef.current.getContents()

        for (let i = 0; i < deltaContent.ops.length; i++) {
            const op = deltaContent.ops[i]
            const { insert } = op
            const { mention, url } = insert

            if (mention) {
                if (mention.userId !== NOT_USER_MENTIONED) {
                    const objectType = TasksHelper.getPeopleTypeUsingId(mention.userId, innerProjectId)
                    const contactUrl = `${window.origin}${getDvMainTabLink(innerProjectId, mention.userId, objectType)}`
                    if (initialContactsUrls.indexOf(contactUrl) < 0) {
                        initialContactsUrls.push(contactUrl)
                    }
                }
            } else if (url) {
                if (isValidTaskLink(url.url, innerProjectId) && initialLinkedTasksUrls.indexOf(url.url) < 0)
                    initialLinkedTasksUrls.push(url.url)
                else if (isValidNoteLink(url.url, innerProjectId) && initialNotesUrls.indexOf(url.url) < 0)
                    initialNotesUrls.push(url.url)
                else if (isValidContactLink(url.url, innerProjectId) && initialContactsUrls.indexOf(url.url) < 0)
                    initialContactsUrls.push(url.url)
                else if (isValidProjectLink(url.url, innerProjectId) && initialProjectsUrls.indexOf(url.url) < 0)
                    initialProjectsUrls.push(url.url)
                else if (isValidGoalLink(url.url, innerProjectId) && initialGoalsUrls.indexOf(url.url) < 0)
                    initialGoalsUrls.push(url.url)
                else if (isValidSkillLink(url.url, innerProjectId) && initialSkillsUrls.indexOf(url.url) < 0)
                    initialSkillsUrls.push(url.url)
                else if (isValidAssistantLink(url.url, innerProjectId) && initialAssistantUrls.indexOf(url.url) < 0)
                    initialAssistantUrls.push(url.url)
            }
        }
        setInitialLinkedObject(
            initialLinkedTasksUrls,
            initialNotesUrls,
            initialContactsUrls,
            initialProjectsUrls,
            initialGoalsUrls,
            initialSkillsUrls,
            initialAssistantUrls
        )
    }

    // Which inputs may hold a file at all — the chat composer, the rich comment modal and the
    // task description ask for these formats; a title or search field does not (AT-2441).
    const supportsAttachments =
        !!otherFormats && otherFormats.some(format => format === 'attachment' || format === 'customImageFormat')

    const attachmentButtonEnabled = showAttachmentButton && supportsAttachments && !!innerProjectId && !disabledEdition

    const inlineControlsStacked = stackInlineControls && attachmentButtonEnabled && dictationEnabled

    useEffect(() => {
        if (!attachmentButtonEnabled || !editorElement) return
        // A stacked column is one control wide, so it reserves the same strip as a single control.
        const className =
            dictationEnabled && !inlineControlsStacked
                ? 'ql-editorWithAttachmentAndDictation'
                : 'ql-editorWithAttachment'
        editorElement.classList.add(className)
        return () => editorElement.classList.remove(className)
    }, [attachmentButtonEnabled, dictationEnabled, editorElement, inlineControlsStacked])

    const addAttachmentTag = (text, uri) => {
        if (!quillRef.current || !attachmentButtonEnabled) return
        insertAttachmentInsideEditor(selectionRef.current.index, quillRef.current, text, uri)
    }

    const getFormats = () => {
        if (!disabledTags && !inMentionsEditionTag) {
            const formats = otherFormats ? [...ALLOWED_FORMATS, ...otherFormats] : [...ALLOWED_FORMATS]
            if (inGenericTask) {
                formats.push('commentTagFormat')
            }
            if (isCalendarTask) {
                formats.push('bold', 'italic', 'underline', 'list', 'attachment')
            }
            return formats
        }
        return []
    }

    const onKeyDown = event => {
        const { key, ctrlKey, shiftKey } = event
        if (
            key === 'Delete' &&
            mentionLastHalfTextRef.current &&
            selectionRef.current.index === textRef.current.length - mentionLastHalfTextRef.current.length
        ) {
            mentionLastHalfTextRef.current = mentionLastHalfTextRef.current.substring(1)
        }

        if (showMentionPopupRef.current) {
            const isPaste = ctrlKey && (key === 'v' || key === 'V')
            if (isPaste) {
                event.preventDefault()
            }
        }

        if (keyboardType === 'numeric') {
            const isPaste = ctrlKey && (key === 'v' || key === 'V')
            const digitRegExp = /\d/
            const isDelete = key === 'Backspace' || key === 'Delete'

            if (!digitRegExp.test(key) && !isPaste && !isDelete) {
                event.preventDefault()
            }
        }

        if (onKeyPress) {
            onKeyPress(key)
        }

        if (key === 'Enter' && singleLine) {
            event.preventDefault()
        }

        if (key === 'Enter' && !singleLine && !disabledEnterKey) {
            event.preventDefault()
            if ((shiftKey || allowPlainEnterBreakLines) && quillRef.current.hasFocus()) {
                const delta = new Delta()
                delta.retain(selectionRef.current.index)
                delta.insert('\n')
                quillRef.current.updateContents(delta, 'user')
                quillRef.current.setSelection(selectionRef.current.index + 1, 0, 'user')
            }
        }

        if ((key === 'ArrowUp' || key === 'ArrowDown' || key === 'Enter') && showMentionPopupRef.current) {
            event.preventDefault()
        }
    }

    const unmountComponent = () => {
        dispatch(setIsQuillTagEditorOpen(false))

        // Quill 2 has no destroy(), so it never tells a blot that the editor around it is
        // gone — every React root mounted inside an embed would outlive this input together
        // with its redux subscription. `detach()` only covers a blot that is deleted from a
        // LIVING document; this is the other half.
        unmountEmbedReactRoots(quillRef.current?.root)

        if (!inMentionsEditionTag) {
            cleanTagsInteractionsPopus()
        }
    }

    useEffect(() => {
        if (quillRef.current) {
            showMentionPopupRef.current
                ? delete quillRef.current.keyboard.bindings['Tab']
                : (quillRef.current.keyboard.bindings['Tab'] = quillKeyboardBindingsTabRef.current)
        }
    }, [showMentionPopupRef.current])

    useEffect(() => {
        if (containerElement) {
            if (styleTheme === SEARCH_THEME) {
                if (isMobileView) {
                    containerElement.classList.add('ql-searchTextInputContainer')
                    editorElement.classList.add('ql-modalTextInputEditor')
                } else {
                    containerElement.classList.remove('ql-searchTextInputContainer')
                    editorElement.classList.remove('ql-modalTextInputEditor')
                }
            }
        }
    }, [isMobileView])

    useEffect(() => {
        if (editorElement) {
            // The outer ScrollView owns scrolling for auto-expanding editors.
            // Giving Quill its own scrollbar changes the line-wrapping width at
            // the threshold and can make the measured height alternate forever.
            editorElement.style.overflowY = autoExpand ? 'visible' : scrollEnabled ? 'auto' : 'hidden'
        }
    }, [autoExpand, editorElement, scrollEnabled])

    useEffect(() => {
        if (containerElement && editorElement && autoExpand) {
            // Quill defaults both elements to height: 100%. Measuring that
            // constrained height and feeding it back into the wrapper creates
            // an oscillation at line-wrap and scrollbar boundaries.
            containerElement.style.height = 'auto'
            editorElement.style.height = 'auto'
        }
    }, [autoExpand, containerElement, editorElement])

    useEffect(() => {
        if (!autoExpand || !editorElement) return

        const requestFrame = callback =>
            typeof window !== 'undefined' && window.requestAnimationFrame
                ? window.requestAnimationFrame(callback)
                : setTimeout(callback, 0)
        const cancelFrame = frame =>
            typeof window !== 'undefined' && window.cancelAnimationFrame
                ? window.cancelAnimationFrame(frame)
                : clearTimeout(frame)
        const measurement = createAutoExpandMeasurement({
            measure: () => getNaturalEditorSize(editorElement),
            report: (width, height) => onContentSizeChangeRef.current?.(width, height),
            requestFrame,
            cancelFrame,
        })
        autoExpandMeasurementRef.current = measurement

        const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measurement.request) : null
        resizeObserver?.observe(editorElement)
        if (typeof window !== 'undefined') window.addEventListener('resize', measurement.request)

        let active = true
        if (typeof document !== 'undefined' && document.fonts?.ready) {
            document.fonts.ready.then(() => active && measurement.request())
        }
        measurement.request()

        return () => {
            active = false
            resizeObserver?.disconnect()
            if (typeof window !== 'undefined') window.removeEventListener('resize', measurement.request)
            measurement.cancel()
            autoExpandMeasurementRef.current = null
        }
    }, [autoExpand, editorElement])

    useEffect(() => {
        if (autoExpand) autoExpandMeasurementRef.current?.request()
    }, [autoExpand, html])

    useEffect(() => {
        document.addEventListener('keydown', onKeyDown)
        return () => {
            document.removeEventListener('keydown', onKeyDown)
        }
    })

    useEffect(() => {
        return () => {
            const deltaContent = quillRef.current.getContents()
            for (let i = 0; i < deltaContent.ops.length; i++) {
                const { hashtag } = deltaContent.ops[i].insert
                if (hashtag) {
                    Backend.unwatchHastagsColors(hashtag.id)
                }
            }
        }
    }, [])

    useEffect(() => {
        dispatch(setQuillTextInputProjectIdsByEditorId(editorId, projectId))
        quillTextInputProjectIds[editorId] = projectId
        quillTextInputIsCalendarTask[editorId] = isCalendarTask
        return () => {
            dispatch(setQuillTextInputProjectIdsByEditorId(editorId, ''))
            delete quillTextInputProjectIds[editorId]
            delete quillTextInputIsCalendarTask[editorId]
        }
    }, [editorId])

    useEffect(() => {
        if (!disabledTags && !inMentionsEditionTag) {
            dispatch(setQuillEditorProjectId(innerProjectId))
        }

        quillRef.current = reactQuillRef.current.getEditor()
        if (autoFocus) quillRef.current.focus()
        quillKeyboardBindingsTabRef.current = quillRef.current.keyboard.bindings['Tab']

        if (disabledTabKey) {
            delete quillRef.current.keyboard.bindings['Tab']
        }

        if (setEditor) {
            setEditor(quillRef.current)
        }

        const containerElement = document.getElementsByClassName(`ql-container-${editorId}`)[0]
        containerElement.classList.add('ql-textInputContainer')

        // Confine Quill's scroll-into-view to this input's own scroll view.
        // Without it, `focus()` / `setSelection()` scroll every scroll container
        // above the editor as well — the task list, a detailed view, the page —
        // which is what makes the app jump when a task line turns into an input
        // (AT-2220, see quill2Setup.js). The boundary is the CustomScrollView
        // wrapper below, so the editor still scrolls itself to follow the caret.
        quillRef.current.scrollBoundaryElement = document.getElementById(scrollBoundaryId)

        const editorElement = document.getElementsByClassName(`ql-editor-${editorId}`)[0]
        editorElement.classList.add('ql-textInputEditor')
        if (styleTheme === TASK_THEME) {
            editorElement.classList.add('ql-taskTextInputEditor')
            containerElement.classList.add('ql-taskTextInputContainer')
        } else if (styleTheme === GOAL_THEME) {
            editorElement.classList.add('ql-taskTextInputEditor')
            editorElement.classList.add('ql-goalTextInputEditor')
            containerElement.classList.add('ql-taskTextInputContainer')
        } else if (styleTheme === SUBTASK_THEME) {
            editorElement.classList.add('ql-subtaskTextInputEditor')
            containerElement.classList.add('ql-subtaskTextInputContainer')
        } else if (styleTheme === COMMENT_MODAL_THEME) {
            editorElement.classList.add('ql-modalTextInputEditor')
        } else if (styleTheme === NEW_TOPIC_MODAL_THEME) {
            editorElement.classList.add('ql-newTopicModalTextInputEditor')
            editorElement.classList.add('ql-modalTextInputEditor')
        } else if (styleTheme === CREATE_TASK_MODAL_THEME) {
            editorElement.classList.add('ql-createTaskModalTextInputEditor')
            editorElement.classList.add('ql-modalTextInputEditor')
        } else if (styleTheme === CREATE_SUBTASK_MODAL_THEME) {
            containerElement.classList.add('ql-createSubtaskModalTextInputContainer')
            editorElement.classList.add('ql-createSubtaskModalTextInputEditor')
            editorElement.classList.add('ql-modalTextInputEditor')
        } else if (styleTheme === SEARCH_THEME) {
            if (isMobileView) {
                containerElement.classList.add('ql-searchTextInputContainer')
                editorElement.classList.add('ql-modalTextInputEditor')
            }
        } else if (styleTheme === INVITE_THEME_DEFAULT) {
            editorElement.classList.add('ql-inviteDefaultTextInputEditor')
        } else if (styleTheme === INVITE_THEME_MODERN) {
            editorElement.classList.add('ql-inviteModernTextInputEditor')
        } else if (styleTheme === CREATE_PROJECT_THEME_DEFAULT) {
            editorElement.classList.add('ql-modalTextInputEditor')
            containerElement.classList.add('ql-createDefaultProjectTextInputContainer')
        } else if (styleTheme === CREATE_PROJECT_THEME_MODERN) {
            editorElement.classList.add('ql-modalTextInputEditorModern')
            containerElement.classList.add('ql-createModernProjectTextInputContainer')
        }

        if (singleLine) {
            editorElement.classList.add('ql-textInputEditorSingleLine')
        }

        editorElement.addEventListener('copy', event => {
            onCopy(event, quillRef.current, projectId, false)
        })

        editorElement.addEventListener('cut', event => {
            onCopy(event, quillRef.current, projectId, true)
        })

        setEditorElement(editorElement)
        setContainerElement(containerElement)

        initialDeltaOps ? quillRef.current.setContents(initialDeltaOps) : processInitialText()

        if (initialCursorIndex !== null && initialCursorIndex !== undefined) {
            quillRef.current.setSelection(initialCursorIndex, 0)
        }
        return unmountComponent
    }, [])

    // AT-2441. Quill routes a PASTED image file through its uploader, which would insert a
    // base64 `image` embed this input cannot serialize — it is lost the moment the comment is
    // posted (see GatedUploader in quill2Setup). Declare the app's own insert instead, so a
    // pasted file becomes exactly what a dropped one does. Only for inputs that actually accept
    // attachments: a title or search field has no such format and keeps quill's behaviour.
    // Reassigned on every render on purpose — the handler closes over the project and the
    // cursor setter, both of which can change under a live editor.
    useEffect(() => {
        if (!quillRef.current) return
        quillRef.current.appManagedFileUpload = supportsAttachments
            ? createAppManagedFileUpload({
                  editor: quillRef.current,
                  projectId: innerProjectId,
                  setInputCursorIndex,
              })
            : undefined
    })

    useImperativeHandle(ref, () => ({
        getEditorId: () => {
            return editorId
        },
        getEditor: () => {
            return quillRef.current
        },
        focus: () => {
            quillRef.current.focus()
        },
        blur: () => {
            quillRef.current.blur()
        },
        clear: () => {
            document.querySelector(`.ql-container-${editorId} .ql-editor`).innerHTML = ''
            showMentionPopupRef.current = false
            selectionRef.current = { index: 0, length: 0 }
            mentionTextRef.current = ''
            setSelectionBounds({ top: 0, left: 0 })
            textRef.current = ''
            setHtml('')
            mentionStartIndexRef.current = 0
            mentionLastHalfTextRef.current = ''
        },
        isFocused: () => {
            return quillRef.current.hasFocus()
        },
        clearAndSetContent: text => {
            // Clear existing content
            document.querySelector(`.ql-container-${editorId} .ql-editor`).innerHTML = ''
            showMentionPopupRef.current = false
            selectionRef.current = { index: 0, length: 0 }
            mentionTextRef.current = ''
            setSelectionBounds({ top: 0, left: 0 })
            textRef.current = ''
            setHtml('')
            mentionStartIndexRef.current = 0
            mentionLastHalfTextRef.current = ''

            // Set new content
            if (text && text.length > 0) {
                const delta = processPastedFn(
                    text,
                    Delta,
                    innerProjectId,
                    editorId,
                    userEditingTagsId,
                    inGenericTask,
                    genericData,
                    quillRef.current,
                    false,
                    null,
                    false
                )
                quillRef.current.updateContents(delta)
                quillRef.current.setSelection(quillRef.current.getLength(), 0)
                setInitialLinkedObject && getLinkedUrlInitialText()
            }
        },
        triggerMention: () => {
            quillRef.current.focus()
            const range = quillRef.current.getSelection()
            const index = range ? range.index : quillRef.current.getLength()
            quillRef.current.insertText(index, '@', 'user')
            quillRef.current.setSelection(index + 1, 0)
        },
    }))

    return (
        <CustomScrollView
            nativeID={scrollBoundaryId}
            style={[
                containerStyle,
                {
                    height: fixedHeight ? fixedHeight : '100%',
                    maxHeight: maxHeight ? maxHeight : 'auto',
                    overflow: 'hidden',
                },
            ]}
            onContentSizeChange={autoExpand ? undefined : onContentSizeChange}
            scrollEnabled={scrollEnabled}
            showIndicator={showScrollIndicator}
            fixedChildren={
                dictationEnabled || attachmentButtonEnabled ? (
                    <View
                        pointerEvents={'box-none'}
                        testID={'inline-input-controls'}
                        style={
                            inlineControlsStacked
                                ? [localDictationWrapperStyle, localStackedControlsStyle]
                                : localDictationWrapperStyle
                        }
                    >
                        {attachmentButtonEnabled && (
                            <AddFeedAttachButton
                                compact
                                projectId={innerProjectId}
                                isDisabled={!editorElement}
                                addAttachmentTag={addAttachmentTag}
                            />
                        )}
                        {dictationEnabled && (
                            <RambleButton
                                projectId={innerProjectId}
                                targetKind={dictationKind}
                                getCurrentText={() => textRef.current}
                                onTextReady={insertDictatedText}
                                onSubmit={submitDictatedText}
                                visible={dictationButtonVisible}
                                pushToTalkHandoff={pushToTalkHandoff}
                            />
                        )}
                    </View>
                ) : null
            }
        >
            <ReactQuill
                ref={el => {
                    reactQuillRef.current = el
                    quillTextInputRefs[editorId] = el
                }}
                modules={{
                    editorMeta: true,
                    toolbar: false,
                    autoformat: true,
                    history: {
                        maxStack: 100,
                        userOnly: true,
                        beforeUndoRedo,
                    },
                }}
                value={htmlRef.current}
                onChange={updateText}
                placeholder={createPlaceholder(
                    placeholder,
                    QUILL_EDITOR_TEXT_INPUT_TYPE,
                    editorId,
                    keyboardType,
                    singleLine,
                    userEditingTagsId,
                    disabledEnterKey
                )}
                onChangeSelection={onChangeSelection}
                formats={getFormats()}
                readOnly={disabledEdition}
            />

            {showMentionPopupRef.current && (
                <WrapperMentionsModal
                    mentionText={inMentionsEditionTag ? textRef.current.trim() : mentionTextRef.current}
                    selectItemToMention={selectItemToMention}
                    projectId={innerProjectId}
                    contentLocation={selectionBounds}
                    setMentionModalHeight={setMentionModalHeight}
                    keepFocus={() => {
                        quillRef.current.focus()
                    }}
                    inMentionsEditionTag={inMentionsEditionTag}
                    insertNormalMention={insertNormalMention}
                    getMentionViewport={getMentionViewport}
                />
            )}
        </CustomScrollView>
    )
}

// Anchored to the input's visible frame (fixedChildren renders outside the scroll content); right
// offset clears CustomScrollView's 4px scroll indicator. The mic centers on the FIRST text line,
// which sits ~5px below the frame top (that offset is codified by the global placeholder rule
// `.ql-textInputContainer > .ql-editor.ql-blank::before { top: 5px }` in styles.css) with a fixed
// 24px line-height — so a 24px band at top:6 centers the icon on that line in every input, and in
// the task row it lands level with the 24px assignee avatar (avatar top 8 in the outer row, this
// frame at row y=2 → both centers at frame y=18). Frame-centering or bottom-anchoring both drift
// below the text in inputs whose frame is taller than their content (the add-task row has
// minHeight 45 with one line at the top). Box-none so the band never swallows input clicks.
const localDictationWrapperStyle = {
    position: 'absolute',
    right: 8,
    top: 6,
    height: 24,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    justifyContent: 'center',
    zIndex: 10,
}

// AT-2938: the stacked variant keeps the same anchor (top: 6, right: 8), so the first control
// (+) stays level with the first text line and the mic sits directly below it. 24 + 4 + 24 = 52px
// fits inside the 88px stacked composer frame.
const localStackedControlsStyle = {
    flexDirection: 'column',
    height: 'auto',
}

export default forwardRef(CustomTextInput3)
